import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";

/**
 * FeatureContext — loads /api/settings once per authenticated session and
 * exposes `canUse(feature_key)` gated by the current user's role.
 *
 * Semantics:
 *   - Admin always sees everything (defensive default — never let toggles
 *     lock the admin out of settings).
 *   - Unknown roles fall through to `true` so we don't over-restrict.
 *   - The default per role is TRUE — features only hide when explicitly
 *     un-ticked in the Settings matrix.
 *
 * Refresh: fires when auth state changes OR when the caller invokes
 * `refresh()` (used by the Settings page right after saving toggles).
 */
const FeatureCtx = createContext({
  toggles: {},
  canUse: () => true,
  loading: true,
  refresh: async () => {},
});

export function FeatureProvider({ children }) {
  const { user, loading: authLoading } = useAuth();
  const [toggles, setToggles] = useState({});
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (authLoading || !user) { setLoading(false); return; }
    try {
      const { data } = await api.get("/settings");
      setToggles(data?.feature_toggles || {});
    } catch {
      setToggles({});
    } finally { setLoading(false); }
  }, [user, authLoading]);

  useEffect(() => { refresh(); }, [refresh]);

  const canUse = useCallback((featureKey) => {
    if (!featureKey) return true;
    const role = user?.role;
    if (!role) return true;                // pre-auth guard — let it through
    if (role === "admin") return true;     // admins always full access
    const roleMap = toggles?.[role];
    if (!roleMap) return true;             // no toggles saved for this role
    const val = roleMap[featureKey];
    return val === false ? false : true;   // explicit false = hide; anything else = allow
  }, [toggles, user]);

  return (
    <FeatureCtx.Provider value={{ toggles, canUse, loading, refresh }}>
      {children}
    </FeatureCtx.Provider>
  );
}

export const useFeatures = () => useContext(FeatureCtx);
